import { Ajax } from "../Common/Ajax";
import { CookieHelper } from "../Common/CookieHelper";
import { GlobalData } from "../Common/GlobalData";
import { HtmlHelpers } from "../Common/HtmlHelpers";
import { Notification, TemplateHelpers, ValueSwapHelper } from "../Common/KendoHelpers";
import { StockPickList } from "../Common/StockPickList";
import { TooltipHelper, TooltipColumnContent, TooltipElement } from "../Common/TooltipHelper";

export class MovementComparison {
    private static readonly GRID = '#grid';

    private static readonly PERCENT_FIELDS = [
        'topToBottom.gainLossPct',
        'topToFtd.gainLossPct',
        'bottomToToday.gainLossPct',
        'ftdToToday.gainLossPct',
        'topToToday.gainLossPct',
    ];
    private static readonly DOLLAR_FIELDS = [
        'topToBottom.gainLoss',
        'topToFtd.gainLoss',
        'bottomToToday.gainLoss',
        'ftdToToday.gainLoss',
        'topToToday.gainLoss',
    ];

    private readonly symbolLink = 'RelativeStrengthLine?symbol=';

    private stockPickList!: MovementComparisonStockPickList;
    private grid!: kendo.ui.Grid;

    constructor() {
        this.init();
    }

    private init() {
        $('[name=valueSwap]').on('change', (event: any) => {
            if (event.currentTarget.value == '0') {
                ValueSwapHelper.showHideFields(MovementComparison.GRID,
                    MovementComparison.PERCENT_FIELDS, MovementComparison.DOLLAR_FIELDS);
            } else {
                ValueSwapHelper.showHideFields(MovementComparison.GRID,
                    MovementComparison.DOLLAR_FIELDS, MovementComparison.PERCENT_FIELDS);
            }
        });

        this.stockPickList = new MovementComparisonStockPickList(MovementComparison.GRID,
            GlobalData.URL_SUFFIX_TECHNICAL_REPORTS_MOVEMENT_COMPARISON);

        this.grid = $(MovementComparison.GRID).kendoGrid({
            dataSource: new kendo.data.DataSource(this.stockPickList.getDataSourceOptions()),
            dataBound: () => {
                this.initTooltipData();
            },
            height: 915,
            groupable: false,
            sortable: {
                mode: "mixed",
                allowUnsort: true,
                showIndexes: true
            },
            pageable: false,
            columns: [{
                field: 'symbol',
                title: 'Symbol',
                width: '8%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell-tight table-cell-center' },
                template: TemplateHelpers.getSymbolLinkTemplate(this.symbolLink),
            }, {
                field: 'companyName',
                title: 'Company Name',
                width: '20%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12-left' },
                attributes: { 'class': 'table-cell grid-cell-left' },
            }, {
                field: 'close',
                title: 'Close',
                width: '8%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell-tight table-cell-center' },
                format: '{0:c2}',
            }, {
                width: '1%',
                headerAttributes: { 'class': 'table-header-cell' },
                attributes: { 'class': 'grid-column-padding' },
            }, {
                field: 'topToBottom.gainLoss',
                title: 'Top to<br>Bottom<br>Gain/Loss $',
                width: '8%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell-tight table-cell-center' },
                template: kendo.template(TemplateHelpers.getColorCurrencyTemplate('topToBottom.gainLoss')),
            }, {
                field: 'topToBottom.gainLossPct',
                title: 'Top to<br>Bottom<br>Gain/Loss %',
                width: '8%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell-tight table-cell-center' },
                template: kendo.template(TemplateHelpers.getColorPercentTemplate('topToBottom.gainLossPct', 0)),
            }, {
                field: 'topToFtd.gainLoss',
                title: 'Top to<br>FTD<br>Gain/Loss $',
                width: '8%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell-tight table-cell-center' },
                template: kendo.template(TemplateHelpers.getColorCurrencyTemplate('topToFtd.gainLoss')),
            }, {
                field: 'topToFtd.gainLossPct',
                title: 'Top to<br>FTD<br>Gain/Loss %',
                width: '8%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell-tight table-cell-center' },
                template: kendo.template(TemplateHelpers.getColorPercentTemplate('topToFtd.gainLossPct', 0)),
            }, {
                field: 'resilienceScore',
                title: 'Resilience',
                width: '8%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: {
                    'class': 'table-cell-tight table-cell-center table-cell-pointer',
                    'tooltip': 'resilienceScore',
                },
                template: kendo.template(TemplateHelpers.getGradeTemplate('resilienceGrade', 'resilienceScore', 'O')),
            }, {
                width: '1%',
                headerAttributes: { 'class': 'table-header-cell' },
                attributes: { 'class': 'grid-column-padding' },
            }, {
                field: 'bottomToToday.gainLoss',
                title: 'Bottom to<br>Today<br>Gain/Loss $',
                width: '8%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell-tight table-cell-center' },
                template: kendo.template(TemplateHelpers.getColorCurrencyTemplate('bottomToToday.gainLoss')),
            }, {
                field: 'bottomToToday.gainLossPct',
                title: 'Bottom to<br>Today<br>Gain/Loss %',
                width: '8%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell-tight table-cell-center' },
                template: kendo.template(TemplateHelpers.getColorPercentTemplate('bottomToToday.gainLossPct', 0)),
            }, {
                field: 'ftdToToday.gainLoss',
                title: 'FTD to<br>Today<br>Gain/Loss $',
                width: '8%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell-tight table-cell-center' },
                template: kendo.template(TemplateHelpers.getColorCurrencyTemplate('ftdToToday.gainLoss')),
            }, {
                field: 'ftdToToday.gainLossPct',
                title: 'FTD to<br>Today<br>Gain/Loss %',
                width: '8%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell-tight table-cell-center' },
                template: kendo.template(TemplateHelpers.getColorPercentTemplate('ftdToToday.gainLossPct', 0)),
            }, {
                field: 'opportunityScore',
                title: 'Opportunity',
                width: '8%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: {
                    'class': 'table-cell-tight table-cell-center table-cell-pointer',
                    'tooltip': 'opportunityScore',
                },
                template: kendo.template(TemplateHelpers.getGradeTemplate('opportunityGrade', 'opportunityScore', 'O')),
            }, {
                width: '1%',
                headerAttributes: { 'class': 'table-header-cell' },
                attributes: { 'class': 'grid-column-padding' },
            }, {
                field: 'topToToday.gainLoss',
                title: 'Top to<br>Today<br>Gain/Loss $',
                width: '8%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell-tight table-cell-center' },
                template: kendo.template(TemplateHelpers.getColorCurrencyTemplate('topToToday.gainLoss')),
            }, {
                field: 'topToToday.gainLossPct',
                title: 'Top to<br>Today<br>Gain/Loss %',
                width: '8%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell-tight table-cell-center' },
                template: kendo.template(TemplateHelpers.getColorPercentTemplate('topToToday.gainLossPct', 0)),
            }, {
                field: 'overallScore',
                title: 'Overall',
                width: '8%',
                headerAttributes: { 'class': 'table-header-cell grid-header-12' },
                attributes: { 'class': 'table-cell-tight table-cell-center' },
                template: kendo.template(TemplateHelpers.getGradeTemplate('overallGrade', 'overallScore', 'O')),
                }]
        }).data('kendoGrid')!;

        ValueSwapHelper.showHideFields(MovementComparison.GRID,
            MovementComparison.PERCENT_FIELDS, MovementComparison.DOLLAR_FIELDS);

        this.stockPickList.setStockIndexDates();
    }

    protected initTooltipData() {
        const firstDataRow = 2;
        const symbolColumn = 0;

        const data = this.grid.dataSource.data();
        if (!data || data.length == 0) {
            console.error('initTooltipData(): no data');
            return;
        }

        const rows = $(MovementComparison.GRID).find('tr');
        if (rows?.length <= firstDataRow) {
            return;
        }

        let ttCellIndices = TooltipHelper.getCellIndices(rows, firstDataRow);
        if ($.isEmptyObject(ttCellIndices)) {
            return;
        }

        rows.each((rowIndex: number, rowElement: HTMLTableRowElement) => {
            const cellElements = rowElement.cells;

            // Skip over the group header rows.
            if (rowIndex < firstDataRow) {
                return;
            }

            const rowData = TooltipHelper.getRowData(
                cellElements[symbolColumn].innerText, data);
            if (!rowData) {
                return;
            }

            // Get the data to be displayed in this row's tooltips and pass it into
            // TooltipHelper so it can format it and set up the Kendo Tooltips.
            const ttContent = new Array<TooltipColumnContent>();
            let ttRowContent: TooltipColumnContent;

            if (rowData.resilienceScore) {
                const cell = cellElements[ttCellIndices['resilienceScore']];
                ttRowContent = new TooltipColumnContent();
                ttRowContent.cellElement = cell;
                ttRowContent.ttElement = [];
                ttRowContent.ttElement.push(new TooltipElement(
                    'RS at Bottom', kendo.toString(rowData.relativeStrengthAtBottom, 'p0')));
                ttRowContent.ttElement.push(new TooltipElement(
                    'RS before FTD', kendo.toString(rowData.relativeStrengthBeforeFtd, 'p0')));
                ttRowContent.ttElement.push(new TooltipElement(
                    'RS Max Before Bottom', kendo.toString(rowData.maxCorrectionRelativeStrength, 'p0')));
                ttRowContent.ttElement.push(new TooltipElement(
                    'RS Min Before Bottom', kendo.toString(rowData.minCorrectionRelativeStrength, 'p0')));
                ttRowContent.ttElement.push(new TooltipElement(
                    'RS Change Before Bottom', kendo.toString(rowData.correctionRelativeStrengthChange, 'p0')));
                ttRowContent.ttElement.push(new TooltipElement(
                    'RS Change Bottom to FTD', kendo.toString(rowData.bottomToFtdRelativeStrengthChange, 'p0')));
                ttContent.push(ttRowContent);
            }

            if (rowData.opportunityScore) {
                const cell = cellElements[ttCellIndices['opportunityScore']];
                ttRowContent = new TooltipColumnContent();
                ttRowContent.cellElement = cell;
                ttRowContent.ttElement = [];
                ttRowContent.ttElement.push(new TooltipElement(
                    'RS at FTD', kendo.toString(rowData.relativeStrengthAtFtd, 'p0')));
                ttRowContent.ttElement.push(new TooltipElement(
                    'RS Max Post-FTD', kendo.toString(rowData.maxPostFtdRelativeStrength, 'p0')));
                ttRowContent.ttElement.push(new TooltipElement(
                    'RS Min Post-FTD', kendo.toString(rowData.minPostFtdRelativeStrength, 'p0')));
                ttRowContent.ttElement.push(new TooltipElement(
                    'RS Change Post-FTD', kendo.toString(rowData.postFtdRelativeStrengthChange, 'p0')));
                ttContent.push(ttRowContent);
            }

            TooltipHelper.createRowOfTooltips(ttContent);
        });
    }

}

export class MovementComparisonStockPickList extends StockPickList {
    private static readonly CHOOSE_INDEX = 'input[name="chooseIndex"]';
    private static readonly TOP_DATE_CLOSE = '#topDateClose';
    private static readonly TOP_DATE = '#topDate';
    private static readonly BOTTOM_DATE_CLOSE = '#bottomDateClose';
    private static readonly BOTTOM_DATE = '#bottomDate';
    private static readonly FTD_DATE_CLOSE = '#ftdDateClose';
    private static readonly FTD_DATE = '#ftdDate';

    constructor(gridId: string, apiUrl: string) {
        super(gridId, apiUrl);

        this.createMc();
        this.defineEvents();
    }

    private createMc() {
        const self = this;

        this.dropDownTree.options.change = function () {
            const value = this.value();
            const valueInt = parseInt(value);
            if (!isNaN(valueInt)) {
                self.updateGrid(valueInt);
            }
        };
    }

    private defineEvents() {
        const self = this;

        $(MovementComparisonStockPickList.TOP_DATE).kendoDatePicker({
            change: (e: any) => {
                self.updateDateAndClose('top', e.sender.value());
            }
        });

        $(MovementComparisonStockPickList.BOTTOM_DATE).kendoDatePicker({
            change: (e: any) => {
                self.updateDateAndClose('bottom', e.sender.value());
            }
        });

        $(MovementComparisonStockPickList.FTD_DATE).kendoDatePicker({
            change: (e: any) => {
                self.updateDateAndClose('ftd', e.sender.value());
            }
        });

        $(MovementComparisonStockPickList.CHOOSE_INDEX).on('change', () => {
            this.setStockIndexDates();
            this.updateGrid();
        });
    }

    private getSelectedStockIndex(): string {
        return $(MovementComparisonStockPickList.CHOOSE_INDEX + ':checked').val() as string;
    }

    public select(value: number) {
        this.dropDownTree.value(value);
        this.updateGrid(value);
    }

    private updateDateAndClose(date: string, value: Date | null) {
        if (!value) {
            return;
        }

        const success = (stockIndexData: any) => {
            this.refreshStockIndexDates(stockIndexData);
            this.updateGrid();
        };

        const error = () => {
            const notification = new Notification();
            notification.error('Unable to update the date');
        }

        const symbol = this.getSelectedStockIndex();
        const formattedValue = kendo.toString(value, 'yyyy-MM-dd');

        Ajax.invoke(GlobalData.URL_SUFFIX_TECHNICAL_REPORTS_MOVEMENT_COMPARISON +
            `/UpdateDateAndClose?symbol=${encodeURIComponent(symbol)}` +
            `&date=${encodeURIComponent(date)}` +
            `&value=${encodeURIComponent(formattedValue)}`,
            'PUT', null, success, error);
    }

    public updateGrid(index: number | null = null) {
        if (!index) {
            index = this.dropDownTree.value();
        }

        this.setDataSourceOptions(index);
        const grid = $(this.gridId).data("kendoGrid");
        if (grid) {
            grid.setDataSource(new kendo.data.DataSource(this.dataSourceOptions));
        }
    }

    protected setDataSourceOptions(index: number | null): void {
        this.dataSourceOptions = {
            transport: {
                read: GlobalData.URL_BASE + this.apiUrl + '?index=' + index + 
                    '&symbol=' + this.getSelectedStockIndex(),
            },
            error: (e) => {
                const notification = new Notification();
                notification.error('No data found to display');
            },
        };
        CookieHelper.set(StockPickList.COOKIE, index);
    }

    public setStockIndexDates() {
        const selectedStockIndex = this.getSelectedStockIndex();

        const success = (stockIndexData: any) => {
            this.refreshStockIndexDates(stockIndexData);
        };

        Ajax.invoke(GlobalData.URL_SUFFIX_TECHNICAL_REPORTS_MOVEMENT_COMPARISON +
            '/StockIndexData?symbol=' + selectedStockIndex, 'GET', null, success);
    }

    private refreshStockIndexDates(stockIndexData: any) {
        if (stockIndexData) {
            $(MovementComparisonStockPickList.TOP_DATE_CLOSE).html(
                HtmlHelpers.getFormattedCurrency(stockIndexData.topDateClose));
            this.setDatePickerValue(MovementComparisonStockPickList.TOP_DATE,
                stockIndexData.topDate);

            $(MovementComparisonStockPickList.BOTTOM_DATE_CLOSE).html(
                HtmlHelpers.getFormattedCurrency(stockIndexData.bottomDateClose));
            this.setDatePickerValue(MovementComparisonStockPickList.BOTTOM_DATE,
                stockIndexData.bottomDate);

            $(MovementComparisonStockPickList.FTD_DATE_CLOSE).html(
                HtmlHelpers.getFormattedCurrency(stockIndexData.ftdClose));
            this.setDatePickerValue(MovementComparisonStockPickList.FTD_DATE,
                stockIndexData.ftd);
        }
    }

    private setDatePickerValue(selector: string, dateValue: string) {
        const datePicker = $(selector).data('kendoDatePicker') as kendo.ui.DatePicker;
        if (datePicker) {
            datePicker.value(new Date(dateValue));
        }
    }
}
